import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  LayersControl,
} from 'react-leaflet';
import L from 'leaflet';
import { Box, Typography, Paper, Chip, alpha, useTheme, Theme } from '@mui/material';
import ContainerMapDetailPanel from './ContainerMapDetailPanel';
import { RemoveAttributionControl } from './RemoveAttributionControl';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';
import { ContainerSchema, ContainerStatus } from '../../types/container.type';
import { calculateFillRate } from '../../helpers/container.helpers';

interface Props {
  containers: ContainerSchema[];
  onEdit: (container: ContainerSchema) => void;
  onDelete: (id: string) => void;
  onQr: (container: ContainerSchema) => void;
  tourMapHint?: boolean;
  tourInfoHint?: boolean;
  onTourMapNext?: () => void;
  onTourInfoNext?: () => void;
  onTourDismiss?: () => void;
  /** Knowledge Base row-action / QR tips. */
  helpRowActionsPhase?: 'edit' | 'delete' | null;
  helpQrHint?: boolean;
  onHelpRowActionsNext?: () => void;
  onHelpRowActionsDismiss?: () => void;
  onHelpQrDismiss?: () => void;
}

const DEFAULT_CENTER: [number, number] = [24.7136, 46.6753];

const MapBounds: React.FC<{ markers: ContainerSchema[] }> = ({ markers }) => {
  const map = useMap();
  React.useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(
        markers.map((m) => [m.latitude as number, m.longitude as number] as L.LatLngTuple)
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [markers, map]);
  return null;
};

const FlyToOnSelect: React.FC<{
  position: [number, number] | null;
  zoom?: number;
}> = ({ position, zoom }) => {
  const map = useMap();

  React.useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, { duration: 0.5 });
    }
  }, [position, zoom, map]);

  return null;
};

/** Leaflet needs invalidateSize when the map box is resized / spotlighted. */
const InvalidateMapSize: React.FC<{ nonce?: string | number | boolean }> = ({
  nonce,
}) => {
  const map = useMap();

  React.useEffect(() => {
    const run = () => {
      try {
        map.invalidateSize({ animate: false });
      } catch {
        /* map may be mid-teardown */
      }
    };
    const t0 = window.setTimeout(run, 0);
    const t1 = window.setTimeout(run, 80);
    const t2 = window.setTimeout(run, 250);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [map, nonce]);

  return null;
};

const iconCache = new Map<string, L.DivIcon>();

const getContainerIcon = (color: string, isPulsing: boolean): L.DivIcon => {
  const cacheKey = `${color}-${isPulsing}`;
  const cached = iconCache.get(cacheKey);
  if (cached) return cached;

  const pulseHtml = isPulsing
    ? `<span style="
        position: absolute;
        top: 50%;
        left: 50%;
        width: 48px;
        height: 48px;
        background: ${color};
        border-radius: 50%;
        transform: translate(-50%, -50%);
        opacity: 0.4;
        animation: pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
      "></span>`
    : '';

  const icon = L.divIcon({
    className: 'container-marker',
    html: `
      <div style="position: relative; width: 48px; height: 48px;">
        ${pulseHtml}
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          width: 48px;
          height: 48px;
          background: ${color};
          border-radius: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 4px 10px rgba(0,0,0,0.35);
          z-index: 2;
        ">
          <img
            src="/icons/container.svg"
            style="
              width: 20px;
              height: 20px;
              filter: brightness(0) invert(1);
            "
          />
        </div>
      </div>
    `,
    iconSize: [48, 48],
    iconAnchor: [24, 48],
    popupAnchor: [0, -49],
  });

  iconCache.set(cacheKey, icon);
  return icon;
};

const getMarkerColor = (
  status: ContainerStatus,
  fillRate: number | null,
  theme: Theme
): string => {
  if (status === ContainerStatus.INACTIVE) return theme.palette.error.main;
  if (fillRate != null && fillRate >= 90) return theme.palette.error.main;
  if (status === ContainerStatus.MAINTENANCE) return theme.palette.grey[500];
  if (status === ContainerStatus.READY_FOR_PICKUP || status === ContainerStatus.AWAITING_PICKUP) {
    return theme.palette.warning.main;
  }
  if (status === ContainerStatus.EMPTY) return theme.palette.success.main;
  return theme.palette.info.main;
};

const ContainerMapView: React.FC<Props> = ({
  containers,
  onEdit,
  onDelete,
  onQr,
  tourMapHint = false,
  tourInfoHint = false,
  onTourMapNext,
  onTourInfoNext,
  onTourDismiss,
  helpRowActionsPhase = null,
  helpQrHint = false,
  onHelpRowActionsNext,
  onHelpRowActionsDismiss,
  onHelpQrDismiss,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedContainer, setSelectedContainer] = useState<ContainerSchema | null>(null);
  const [flyToPosition, setFlyToPosition] = useState<[number, number] | null>(null);
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);

  const validContainers = useMemo(
    () =>
      containers.filter(
        (c) => typeof c.latitude === 'number' && typeof c.longitude === 'number'
      ),
    [containers]
  );

  const helpActionsActive = Boolean(helpRowActionsPhase) || helpQrHint;

  // Self-setup / Help: open the detail panel so action tips have a target.
  useEffect(() => {
    if (!tourInfoHint && !helpActionsActive) return;
    if (selectedContainer) return;
    const first = validContainers[0];
    if (!first) return;
    setSelectedContainer(first);
    setFlyToPosition([first.latitude!, first.longitude!]);
  }, [tourInfoHint, helpActionsActive, validContainers, selectedContainer]);

  const handleClosePanel = () => {
    if (tourInfoHint || helpActionsActive) return;
    setFlyToPosition(null);
    setSelectedContainer(null);
  };

  if (validContainers.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <SetupCoachmark
          open={tourMapHint || tourInfoHint || helpActionsActive}
          fullWidth
          placement={tourMapHint || tourInfoHint ? 'top' : undefined}
          targetSx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'transparent',
            boxShadow: 'none',
            outline: 'none',
            p: 0,
          }}
          title={
            helpActionsActive
              ? helpQrHint
                ? t('containers.printQrEmptyTitle')
                : t('containers.editDeleteContainerEmptyTitle')
              : tourInfoHint
                ? t('selfSetup.tour.containersInfoTitle')
                : t('selfSetup.tour.containersMapTitle')
          }
          subtitle={
            helpActionsActive
              ? helpQrHint
                ? t('containers.printQrEmptyHint')
                : t('containers.editDeleteContainerEmptyHint')
              : tourInfoHint
                ? t('selfSetup.tour.containersInfoSubtitle')
                : t('selfSetup.tour.containersMapSubtitle')
          }
          nextLabel={
            helpActionsActive ? t('selfSetup.tourDone') : t('selfSetup.tourNext')
          }
          onNext={() => {
            if (helpActionsActive) {
              if (helpQrHint) onHelpQrDismiss?.();
              else onHelpRowActionsDismiss?.();
              return;
            }
            if (tourInfoHint) onTourInfoNext?.();
            else onTourMapNext?.();
          }}
          onDismiss={() => {
            if (helpActionsActive) {
              if (helpQrHint) onHelpQrDismiss?.();
              else onHelpRowActionsDismiss?.();
              return;
            }
            onTourDismiss?.();
          }}
        >
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.action.hover, 0.4),
              minHeight: 400,
            }}
          >
            <Typography color="text.secondary">{t('containers.map.noData')}</Typography>
          </Paper>
        </SetupCoachmark>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <SetupCoachmark
        open={tourMapHint}
        fullWidth
        placement="top"
        targetSx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: 'transparent',
          boxShadow: 'none',
          outline: tourMapHint ? `3px solid ${theme.palette.common.white}` : 'none',
          outlineOffset: tourMapHint ? '2px' : 0,
          p: 0,
        }}
        title={t('selfSetup.tour.containersMapTitle')}
        subtitle={t('selfSetup.tour.containersMapSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => onTourMapNext?.()}
        onDismiss={() => onTourDismiss?.()}
      >
        <Box
          ref={mapWrapperRef}
          sx={{
            flex: 1,
            minHeight: 360,
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            position: 'relative',
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              insetInlineStart: 12,
              zIndex: 1000,
            }}
          >
            <Chip
              label={t('containers.map.containersOnMap', { count: validContainers.length })}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.92),
                backdropFilter: 'blur(8px)',
                fontWeight: 600,
                boxShadow: theme.shadows[2],
              }}
            />
          </Box>

          <MapContainer
            center={DEFAULT_CENTER}
            zoom={12}
            style={{ height: '100%', width: '100%', minHeight: 360 }}
            scrollWheelZoom
            attributionControl={false}
          >
            <MapBounds markers={validContainers} />
            <InvalidateMapSize nonce={tourMapHint} />
            <FlyToOnSelect position={flyToPosition} zoom={selectedContainer ? 15 : 11} />
            <RemoveAttributionControl />

            <LayersControl position="topright">
              <LayersControl.BaseLayer checked={!isDark} name={t('containers.map.layerStandard')}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer checked={isDark} name={t('containers.map.layerDark')}>
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name={t('containers.map.layerSatellite')}>
                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
              </LayersControl.BaseLayer>
            </LayersControl>

            {validContainers.map((container) => {
              const fillRate = calculateFillRate(
                container.fill_level,
                container.data?.capacity,
                container.data?.capacity_unit
              );
              const color = getMarkerColor(container.status, fillRate, theme);

              return (
                <Marker
                  key={container.id}
                  position={[container.latitude!, container.longitude!]}
                  icon={getContainerIcon(color, true)}
                  eventHandlers={{
                    click: () => {
                      setFlyToPosition([container.latitude!, container.longitude!]);
                      setSelectedContainer(container);
                    },
                  }}
                />
              );
            })}
          </MapContainer>

          {selectedContainer && (
            <SetupCoachmark
              open={tourInfoHint}
              title={t('selfSetup.tour.containersInfoTitle')}
              subtitle={t('selfSetup.tour.containersInfoSubtitle')}
              nextLabel={t('selfSetup.tourNext')}
              onNext={() => onTourInfoNext?.()}
              onDismiss={() => onTourDismiss?.()}
              targetSx={{
                position: 'absolute',
                top: 0,
                insetInlineEnd: 0,
                height: '100%',
                width: { xs: '100%', sm: 360, md: 400 },
                maxWidth: '100%',
                zIndex: 1100,
                p: 0,
                display: 'block',
                borderRadius: 0,
                overflow: 'hidden',
              }}
            >
              <ContainerMapDetailPanel
                container={selectedContainer}
                onClose={handleClosePanel}
                onEdit={onEdit}
                onDelete={onDelete}
                onQr={onQr}
                embedded
                helpRowActionsPhase={helpRowActionsPhase}
                helpQrHint={helpQrHint}
                onHelpRowActionsNext={onHelpRowActionsNext}
                onHelpRowActionsDismiss={onHelpRowActionsDismiss}
                onHelpQrDismiss={onHelpQrDismiss}
              />
            </SetupCoachmark>
          )}
        </Box>
      </SetupCoachmark>
    </Box>
  );
};

export default ContainerMapView;
